import type { ComponentPropsWithoutRef, FC, ReactNode } from 'react';
import type { VariantProps } from 'class-variance-authority';
import { mainButtonVariants } from '@/shared/ui/Buttons/variants.ts';
import { Link, type LinkProps } from '@tanstack/react-router';
import { cn } from '@/shared/styles/utils.ts';
import { Spinner } from '@/shared/ui/Spinner';

type CommonProps = VariantProps<typeof mainButtonVariants> & {
  loading?: boolean;
  className?: string;
  children?: ReactNode;
};

type ButtonMode = CommonProps &
  Omit<ComponentPropsWithoutRef<'button'>, 'className' | 'children'> & {
    to?: never;
  };

export type LinkMode = CommonProps &
  Omit<LinkProps, 'className' | 'children'> & {
    to: LinkProps['to'];
    onClick?: ComponentPropsWithoutRef<'a'>['onClick'];
  };

export type MainButtonProps = ButtonMode | LinkMode;

export const MainButton: FC<MainButtonProps> = (props) => {
  const {
    variant,
    size,
    loading = false,
    className,
    children,
    disabled,
  } = props;
  const classes = cn(mainButtonVariants({ variant, size }), className);

  if ('to' in props) {
    return (
      <Link {...(props as LinkMode)} to={props.to} className={classes}>
        {children}
      </Link>
    );
  }

  return (
    <button
      {...(props as ButtonMode)}
      className={classes}
      disabled={disabled || loading}
    >
      <div
        className={cn(loading && 'opacity-0 transition-opacity duration-150')}
      >
        {children}
      </div>
      {loading && (
        <Spinner
          className="absolute top-1/2 -translate-y-1/2"
          size={30}
          narrow
          light
        />
      )}
    </button>
  );
};
